export type AccommodationsDefaultLocation = Readonly<{
  lat: number;
  long: number;
  name: string;
  placeId: string;
}>;

/**
 * Default location used for accommodations experiences when an event does not have a usable location anchor.
 */
export const ACCOMMODATIONS_DEFAULT_LOCATION: AccommodationsDefaultLocation = {
  lat: 40.7127753,
  long: -74.0059728,
  name: 'New York, NY, USA',
  placeId: 'ChIJOwg_06VPwokRYv534QaPC8g',
};

/**
 * The curated tag the honeymoon surfaces lead with, matching `SUPPORTED_TAGS` in
 * travel_inventory_service. Shared rather than owned by the honeymoon app because the in-event
 * entry points (the cash-fund "Unlock Offers" CTA) live outside it and must ask for the same
 * string — tags are matched by exact comparison, so a second spelling would silently lift nothing.
 */
export const HONEYMOON_TAG = 'honeymoon';

/** The honeymoon tag as a scenario tag list. Module scope: one identity, so memos downstream hold. */
export const HONEYMOON_PRIORITIZE_TAGS: ReadonlyArray<string> = [HONEYMOON_TAG];

/**
 * The trip-type tags, matching `SUPPORTED_TAGS` in travel_inventory_service. Independent of
 * honeymoon and of each other: a hotel can carry any combination. Order is display order in the
 * tag filter.
 */
export const TRIP_TYPE_TAGS: ReadonlyArray<string> = ['beach', 'wellness', 'nature', 'luxury', 'adventure'];

/**
 * Every tag the honeymoon surfaces let a guest filter by: the honeymoon premise plus the trip
 * types, one flat "Show only" list. Offered, not seeded — only `HONEYMOON_PRIORITIZE_TAGS` is
 * seeded into the URL (see useSeedDefaultPrioritizeTags). Selected tags are ANDed by
 * travel_service, so "honeymoon" + "beach" is honeymoon stays on a beach.
 */
export const HONEYMOON_AVAILABLE_PRIORITIZE_TAGS: ReadonlyArray<string> = [HONEYMOON_TAG, ...TRIP_TYPE_TAGS];

/**
 * The query key a hotel search reads its curated-tag hint from (`?prioritizeTag=honeymoon`). The one
 * definition: the console's `PRIORITIZE_TAG_QUERY_PARAM_KEY` and the shared code that carries the tag
 * between pages both read it from here.
 */
export const PRIORITIZE_TAG_PARAM = 'prioritizeTag';

/** Offer and attribution params every stays hop must carry forward (see useRetainSupportedStaysParams). */
export enum TRANSFERRED_STAYS_QUERY_PARAMS {
  discountCode = 'discountCode',
  /** Share Your Stay reward coupon. Kept on every stays link so it is not dropped before checkout. */
  joyReferralRewardCode = 'joyReferralRewardCode',
  /** Share Your Stay recipient code. Kept on every stays link so the guest still gets the discount at checkout. */
  joyReferralCode = 'joyReferralCode',
  // Per-event $0 attribution promotion; `useReservation` sends it, so a hop that drops it charges full price.
  weddingCode = 'weddingCode',
}

/**
 * Where PublicHoneymoonApp is mounted in the top-level router.
 *
 * Shared because the honeymoon pages are event-less: every other hotel route is built from an event
 * handle (`/:eventHandle/hotel/...`), and the honeymoon product has no event to name, so its routes
 * are anchored on this literal instead. Kept next to the router entry it mirrors (see
 * `src/main/router.tsx`) rather than inside the app, so `hotelRoutes` can build links into it
 * without importing an app from shared code.
 */
export const HONEYMOON_APP_BASE_PATH = '/honeymoon';

/**
 * Handle of the generic `stays` event: a real Joy event that exists so a hotel search can run, and
 * a booking can be made, without the visitor having an event of their own. Public honeymoon search
 * lives under it (`/stays/hotel/search?prioritizeTag=honeymoon`), so checkout gets an event id —
 * reservation create requires one.
 */
export const STAYS_EVENT_HANDLE = 'stays';

export const HotelSearchSource = {
  OTA: 'ota',
  GUEST_MAP: 'guest_map',
  ADMIN_MAP: 'admin_map',
} as const;

export type HotelSearchSource = (typeof HotelSearchSource)[keyof typeof HotelSearchSource];

// Sources where the CTA should still appear for sold-out hotels (e.g., to let couples add them to their event)
export const SOLD_OUT_CTA_ALLOWED_SOURCES: ReadonlyArray<HotelSearchSource> = [HotelSearchSource.ADMIN_MAP];

/**
 * Which fetch path produced the price a guest saw.
 *
 * This is a closed type, not a free string. It names one of two code paths. It does not grow with
 * the number of pages the product has. A third value would mean a third fetch path exists, and that
 * must fail at the type system rather than appear quietly in a chart.
 *
 * `SNAPSHOT` names the `travel_rate_snapshot` table the value describes. A snapshot holds one row
 * per joyPlace, regardless of provider. So a snapshot price is a sole-bidder result by construction.
 * The guest can click it and land on a live price from a different provider.
 */
export const PriceSource = {
  SNAPSHOT: 'snapshot',
  LIVE: 'live',
} as const;

export type PriceSource = (typeof PriceSource)[keyof typeof PriceSource];

/**
 * Map layout mode for the accommodations console.
 *
 * - `full`: Map is always visible and takes up a portion of the screen alongside listings.
 *   Use this for desktop views where you want the map and listings side-by-side.
 *
 * - `compact`: Map is hidden by default and can be expanded by the user.
 *   Use this for space-constrained layouts or when you want to prioritize listings visibility.
 *
 * - `mapOnly`: Only the map is shown — no header, filters panel, or listings panel.
 *   Use this for embedded map views (e.g., the admin preview explore area).
 */
export const HotelSearchMapLayout = {
  FULL: 'full',
  COMPACT: 'compact',
  MAP_ONLY: 'mapOnly',
} as const;

export type HotelSearchMapLayout = (typeof HotelSearchMapLayout)[keyof typeof HotelSearchMapLayout];

export const HotelSearchContextParams = {
  source: 'source',
  layout: 'layout',
  selectedHotel: 'selectedHotel',
} as const;

export type HotelSearchContextParams = (typeof HotelSearchContextParams)[keyof typeof HotelSearchContextParams];

export const SortOption = {
  COUPLES_PICKS: 'couple-picks',
  TOP_MATCHES: 'top-matches',
  PRICE_LOW_TO_HIGH: 'price-low-to-high',
  PRICE_HIGH_TO_LOW: 'price-high-to-low',
  MOST_SAVINGS: 'most-savings',
  HIGHEST_DISCOUNT: 'highest-discount',
  DISTANCE: 'distance',
  // Cash-fund variant only — groups the stays the fund applies to at the top (TRAV-3348).
  HIGHEST_FUND_SAVINGS: 'highest-fund-savings',
} as const;

export type SortOption = (typeof SortOption)[keyof typeof SortOption];
